import { computed, ComputedRef } from 'vue';
import { textUtil } from '../../../shared/utils/textUtil';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { conversionStatusTypes } from '../../../shared/enums/conversionStatusTypes';
import { SearchMediaResult } from '../../../shared/models/responses/search/findGeneric.response';
import { buildPlainExcerptAroundHit } from './useSearchPostContentPlainExcerpt';

export const MAX_VISIBLE_TAGS = 4;

export interface UseSearchResultMedia {
  searchQueries: ComputedRef<RegExp[] | string>;
  showTitle: ComputedRef<boolean>;
  showDescription: ComputedRef<boolean>;
  showAlbumDescription: ComputedRef<boolean>;
  showTags: ComputedRef<boolean>;
  descriptionExcerpt: ComputedRef<string>;
  albumDescriptionExcerpt: ComputedRef<string>;
  creatorName: ComputedRef<string>;
  albumTitle: ComputedRef<string>;
  uploadedDate: ComputedRef<string>;
  visibleTags: ComputedRef<SearchMediaResult['tags']>;
  hasMoreTags: ComputedRef<boolean>;
  additionalTagCount: ComputedRef<number>;
  openMedia: () => void;
}

export const useSearchResultMedia = (
  getMedia: () => SearchMediaResult,
  getSearchString: () => string,
  emitOpen: (media: SearchMediaResult) => void
): UseSearchResultMedia => {
  const highlightRegex = computed<RegExp | null>(() => textUtil.buildHighlightRegex(getSearchString()));

  const searchQueries = computed<RegExp[] | string>(() => {
    const regex = highlightRegex.value;
    return regex ? [regex] : '';
  });

  const hasHit = (text?: string | null): boolean => {
    const regex = highlightRegex.value;
    if (!regex || !text) {
      return false;
    }
    return text.search(regex) !== -1;
  };

  const showTitle = computed<boolean>(() => hasHit(getMedia()?.title));
  const showDescription = computed<boolean>(() => hasHit(getMedia()?.description));
  const showAlbumDescription = computed<boolean>(() => hasHit(getMedia()?.albumDescription));

  const descriptionExcerpt = computed<string>(() =>
    buildPlainExcerptAroundHit(getMedia()?.description ?? '', getSearchString())
  );

  const albumDescriptionExcerpt = computed<string>(() =>
    buildPlainExcerptAroundHit(getMedia()?.albumDescription ?? '', getSearchString())
  );

  const creatorName = computed<string>(() => getMedia()?.creator?.name ?? '');
  const albumTitle = computed<string>(() => getMedia()?.albumTitle ?? '');

  const uploadedDate = computed<string>(() => {
    const created = getMedia()?.file?.created;
    return created ? DateTimeUtil.formatDate(created, 'DD. MMM. YYYY') : '';
  });

  const matchingTags = computed<SearchMediaResult['tags']>(() => {
    const tags = getMedia()?.tags ?? [];
    return tags.filter(tag => hasHit(tag.name));
  });

  const showTags = computed<boolean>(() => matchingTags.value.length > 0);

  const visibleTags = computed<SearchMediaResult['tags']>(() => matchingTags.value.slice(0, MAX_VISIBLE_TAGS));

  const additionalTagCount = computed<number>(() => Math.max(matchingTags.value.length - MAX_VISIBLE_TAGS, 0));

  const hasMoreTags = computed<boolean>(() => additionalTagCount.value > 0);

  const openMedia = (): void => {
    const media = getMedia();
    if (!media || media.conversionStatus === conversionStatusTypes.PROCESSING) {
      return;
    }
    emitOpen(media);
  };

  return {
    searchQueries,
    showTitle,
    showDescription,
    showAlbumDescription,
    showTags,
    descriptionExcerpt,
    albumDescriptionExcerpt,
    creatorName,
    albumTitle,
    uploadedDate,
    visibleTags,
    hasMoreTags,
    additionalTagCount,
    openMedia,
  };
};
